import { Box, Button, Modal } from '@nerdwallet/currency';
import SearchIcon from '@nerdwallet/react-icon/material/Search';
import VisibilityIcon from '@nerdwallet/react-icon/material/Visibility';
import VisibilityOffIcon from '@nerdwallet/react-icon/material/VisibilityOff';
import { Fragment, useState } from 'react';

// Interactive tree component for browsing data structure
const DataTreeNode = ({
  data,
  originalData,
  path = '',
  onFieldClick,
  isTopLevel = false,
}: {
  data: any;
  originalData?: any;
  path?: string;
  onFieldClick: (fieldPath: string) => void;
  isTopLevel?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showData, setShowData] = useState(false);

  // Helper function to get original data at path
  const getOriginalDataAtPath = (origData: any, dataPath: string): any => {
    if (!origData || !dataPath) return origData;

    const pathParts = dataPath.split(/[.[\]]/).filter((part) => part !== '');
    let current = origData;

    pathParts.forEach((part) => {
      if (current === null || current === undefined) return;
      // Prevent prototype pollution by checking for dangerous keys
      if (
        part === '__proto__' ||
        part === 'constructor' ||
        part === 'prototype'
      )
        return;
      // Use hasOwnProperty to ensure we're only accessing own properties
      if (
        typeof current === 'object' &&
        Object.prototype.hasOwnProperty.call(current, part)
      ) {
        current = current[part];
      } else {
        current = undefined;
      }
    });

    return current;
  };

  // Helper function to format data value for display
  const formatDataValue = (
    value: any,
    isFullArrayDisplay: boolean = false
  ): string => {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';
    if (typeof value === 'string') return `"${value}"`;
    if (typeof value === 'boolean') return value.toString();
    if (typeof value === 'number') return value.toString();
    if (Array.isArray(value)) {
      if (isFullArrayDisplay) {
        // Show full array content with proper formatting
        try {
          return JSON.stringify(value, null, 2);
        } catch (error) {
          return `[Array with ${value.length} items - unable to stringify]`;
        }
      }
      return `[${value.length} items]`;
    }
    if (typeof value === 'object') {
      if (isFullArrayDisplay) {
        // Show full object content with proper formatting
        try {
          return JSON.stringify(value, null, 2);
        } catch (error) {
          return `{Object with ${
            Object.keys(value).length
          } keys - unable to stringify}`;
        }
      }
      return `{${Object.keys(value).length} keys}`;
    }
    return String(value);
  };

  if (typeof data === 'string') {
    // This is a type string, make it clickable with toggle functionality
    const isArrayType = data.startsWith('Array<');
    const originalValue = originalData
      ? getOriginalDataAtPath(originalData, path)
      : undefined;
    const hasOriginalData =
      originalData !== undefined && originalValue !== undefined;

    return (
      <Box display="flex" alignItems="flex-start" gap="1">
        {hasOriginalData && (
          <Box
            as="span"
            style={{
              cursor: 'pointer',
              color: '#6b7280',
              fontSize: '10px',
              userSelect: 'none',
              alignItems: 'center',
              display: 'flex',
            }}
            onClick={() => setShowData(!showData)}
            title={showData ? 'Show type' : 'Show data'}
          >
            {showData ? (
              <VisibilityOffIcon size="12px" />
            ) : (
              <VisibilityIcon size="12px" />
            )}
          </Box>
        )}
        <Box
          as="span"
          style={{
            color: isArrayType ? '#8b5cf6' : '#059669',
            cursor: 'pointer',
            textDecoration: 'underline',
            fontSize: '12px',
            marginLeft: '4px',
          }}
          onClick={() => onFieldClick(path)}
        >
          {showData && hasOriginalData ? (
            <pre
              style={{
                margin: 0,
                fontFamily: 'inherit',
                whiteSpace: 'pre-wrap',
              }}
            >
              {formatDataValue(originalValue, true)}
            </pre>
          ) : (
            data
          )}
        </Box>
      </Box>
    );
  }

  if (Array.isArray(data)) {
    const originalValue = originalData
      ? getOriginalDataAtPath(originalData, path)
      : undefined;
    const hasOriginalData =
      originalData !== undefined && originalValue !== undefined;

    if (isTopLevel) {
      return (
        <Box>
          <Box
            display="flex"
            alignItems="flex-start"
            style={{ cursor: 'pointer', fontSize: '12px' }}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <span style={{ marginRight: '4px', color: '#6b7280' }}>
              {isExpanded ? '▼' : '▶'}
            </span>
            <span style={{ color: '#374151' }}>
              Array [{data.length} items]
            </span>
          </Box>
          {isExpanded && (
            <Box marginLeft="4" marginTop="1">
              {data.slice(0, 3).map((item, index) => {
                const itemKey = `${path || 'root'}-${index}-${JSON.stringify(
                  item
                ).slice(0, 20)}`;
                return (
                  <Box key={itemKey} marginBottom="1">
                    <Box style={{ color: '#6b7280', fontSize: '11px' }}>
                      [{index}]
                    </Box>
                    <Box marginLeft="2">
                      <DataTreeNode
                        data={item}
                        originalData={originalData}
                        path={`${path}[${index}]`}
                        onFieldClick={onFieldClick}
                        isTopLevel={false}
                      />
                    </Box>
                  </Box>
                );
              })}
              {data.length > 3 && (
                <Box
                  style={{
                    color: '#9ca3af',
                    fontSize: '11px',
                    fontStyle: 'italic',
                  }}
                >
                  ... and {data.length - 3} more items
                </Box>
              )}
            </Box>
          )}
        </Box>
      );
    }
    // For nested arrays, show expand/collapse functionality with show data toggle
    return (
      <Box>
        <Box display="flex" alignItems="flex-start" gap="1">
          {hasOriginalData && (
            <Box
              as="span"
              style={{
                cursor: 'pointer',
                color: '#6b7280',
                fontSize: '10px',
                userSelect: 'none',
                alignItems: 'center',
                display: 'flex',
              }}
              onClick={() => setShowData(!showData)}
              title={showData ? 'Show type' : 'Show data'}
            >
              {showData ? (
                <VisibilityOffIcon size="12px" />
              ) : (
                <VisibilityIcon size="12px" />
              )}
            </Box>
          )}
          <Box
            display="flex"
            alignItems="center"
            style={{ cursor: 'pointer', fontSize: '12px' }}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <span style={{ marginRight: '4px', color: '#6b7280' }}>
              {isExpanded ? '▼' : '▶'}
            </span>
            <span style={{ color: '#8b5cf6' }}>
              {(() => {
                if (showData && hasOriginalData) {
                  return (
                    <pre
                      style={{
                        margin: 0,
                        fontFamily: 'inherit',
                        whiteSpace: 'pre-wrap',
                        display: 'inline',
                      }}
                    >
                      {formatDataValue(originalValue, true)}
                    </pre>
                  );
                }
                if (isExpanded) {
                  return `Array[${data.length}] (click to select path)`;
                }
                return '[...]';
              })()}
            </span>
          </Box>
        </Box>
        {isExpanded && !showData && (
          <Box marginLeft="4" marginTop="1">
            <Box
              style={{
                color: '#8b5cf6',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '11px',
                marginBottom: '4px',
              }}
              onClick={() => onFieldClick(path)}
            >
              → Click here to select this array path
            </Box>
            <Box
              style={{
                color: '#6b7280',
                fontSize: '11px',
                fontStyle: 'italic',
              }}
            >
              Array contains {data.length} items
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  if (typeof data === 'object' && data !== null) {
    const entries = Object.entries(data);

    return (
      <Box>
        <Box
          display="flex"
          alignItems="flex-start"
          style={{ cursor: 'pointer', fontSize: '12px' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <span style={{ marginRight: '4px', color: '#6b7280' }}>
            {isExpanded ? '▼' : '▶'}
          </span>
          <span style={{ color: '#374151' }}>
            {isExpanded ? `Object {${entries.length} fields}` : '{...}'}
          </span>
        </Box>
        {isExpanded && (
          <Box marginLeft="4" marginTop="1">
            {entries.map(([key, value]) => {
              const fieldPath = path ? `${path}.${key}` : key;

              return (
                <Box key={key} marginBottom="1">
                  <Box display="flex" alignItems="flex-start">
                    <Box
                      style={{
                        color: '#1f2937',
                        fontWeight: 'medium',
                        cursor: 'pointer',
                        fontSize: '12px',
                        marginRight: '8px',
                        textDecoration: 'underline',
                      }}
                      onClick={() => onFieldClick(fieldPath)}
                    >
                      {key}:
                    </Box>
                    <Box flex="1">
                      <DataTreeNode
                        data={value}
                        originalData={originalData}
                        path={fieldPath}
                        onFieldClick={onFieldClick}
                        isTopLevel={false}
                      />
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      as="span"
      style={{
        color: '#059669',
        cursor: 'pointer',
        textDecoration: 'underline',
        fontSize: '12px',
      }}
      onClick={() => onFieldClick(path)}
    >
      {String(data)}
    </Box>
  );
};

export const DynamicMarketplaceOfferViewerComponent = ({
  marketplaceOffers,
  isModalOpen,
  setIsModalOpen,
  handleFieldClick,
  openModalForRule,
  overrideButtonIcon,
  overrideButtonStyle,
}: {
  marketplaceOffers: any;
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  handleFieldClick: (fieldPath: string) => void;
  openModalForRule: () => void;
  overrideButtonIcon?: React.ReactNode;
  overrideButtonStyle?: React.CSSProperties;
}) => {
  // Function to transform data to show types instead of values (deep traversal)
  const getDataWithTypes = (data: any, isTopLevel: boolean = true): any => {
    if (Array.isArray(data)) {
      // For top-level array (marketplaceOffers), show each item individually
      if (isTopLevel) {
        return data.map((item) => getDataWithTypes(item, false));
      }

      // For nested arrays, show Array<type> format
      if (data.length === 0) {
        return 'Array<unknown>';
      }

      // Get the type of the first element to represent the array type
      const firstElementType = getDataWithTypes(data[0], false);

      // Check if all elements have the same type structure
      const allSameType = data.every((item) => {
        const itemType = getDataWithTypes(item, false);
        return JSON.stringify(itemType) === JSON.stringify(firstElementType);
      });

      if (allSameType) {
        if (typeof firstElementType === 'string') {
          return `Array<${firstElementType}>`;
        }
        // For object types in nested arrays, just show Array<object>
        return `Array<object>`;
      }

      // Mixed types - show all unique types
      const uniqueTypes = [
        ...new Set(
          data.map((item) => {
            const itemType = getDataWithTypes(item, false);
            if (typeof itemType === 'string') {
              return itemType;
            }
            // For object types in mixed arrays, just show 'object'
            return 'object';
          })
        ),
      ];
      return `Array<${uniqueTypes.join(' | ')}>`;
    }
    if (data !== null && typeof data === 'object') {
      return Object.entries(data).reduce((result: any, [key, value]) => {
        // Prevent prototype pollution by checking for dangerous keys
        if (
          key === '__proto__' ||
          key === 'constructor' ||
          key === 'prototype'
        ) {
          return result;
        }

        if (value === null) {
          result[key] = 'null';
        } else if (Array.isArray(value)) {
          result[key] = getDataWithTypes(value, false);
        } else if (typeof value === 'object') {
          result[key] = getDataWithTypes(value, false);
        } else {
          result[key] = typeof value;
        }
        return result;
      }, {});
    }
    return typeof data;
  };

  return (
    <Fragment>
      <Fragment>
        <Button
          secondary
          onClick={openModalForRule}
          style={{
            minWidth: '44px',
            height: '44px',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            border: 'none',
            boxShadow: 'none',
            outline: 'none',
            ...overrideButtonStyle,
          }}
        >
          {overrideButtonIcon || (
            <SearchIcon
              size="24px"
              style={{
                color: '#059669',
                fontWeight: 'bold',
                strokeWidth: '3',
              }}
            />
          )}
        </Button>
      </Fragment>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        title="Marketplace Offers Data"
        subtitle="Available attributes from marketplaceOffers array"
      >
        <Box
          display="flex"
          flexDirection="column"
          maxHeight="500px"
          overflow="auto"
        >
          <Box marginY="3">
            <Box
              style={{
                fontSize: '12px',
                backgroundColor: '#f9fafb',
                padding: '16px',
                borderRadius: '4px',
                border: '1px solid #e5e7eb',
                maxHeight: '400px',
                overflow: 'auto',
              }}
            >
              <Box
                marginBottom="2"
                style={{
                  fontSize: '11px',
                  color: '#6b7280',
                  fontWeight: 'medium',
                }}
              >
                Click on any field name to select it as your attribute:
              </Box>
              <DataTreeNode
                data={getDataWithTypes(marketplaceOffers)}
                originalData={marketplaceOffers}
                onFieldClick={handleFieldClick}
                isTopLevel
              />
            </Box>
          </Box>
          <Box display="flex" gap="2">
            <Button primary onClick={() => setIsModalOpen(false)}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Fragment>
  );
};
